import { useCallback, useRef, useState } from 'react';
import { createWordBudget, INPUT_TRUNCATION_NOTE } from './wordBudget';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

interface UseChatStreamOptions {
  endpoint?: string;
  context?: string;
  getContext?: () => { context: string; hasTruncatedBlocks?: boolean };
}

interface UseChatStreamReturn {
  messages: ChatMessage[];
  isStreaming: boolean;
  send: (params: { message: string }) => void;
  stop: () => void;
  reset: () => void;
}

const ASK_VENN_ENDPOINT = '/api/ask-venn/chat';

/**
 * Hook for streaming chat with Claude via the backend proxy.
 */
const useChatStream = ({
  endpoint = ASK_VENN_ENDPOINT,
  context,
  getContext,
}: UseChatStreamOptions = {}): UseChatStreamReturn => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const abortControllerRef = useRef<AbortController | undefined>(undefined);
  const nextMsgId = () => crypto.randomUUID();

  const stop = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = undefined;
    setIsStreaming(false);
  }, []);

  const send = useCallback(
    ({ message }: { message: string }) => {
      const trimmed = message.trim();
      if (!trimmed || isStreaming) return;

      const userMessage: ChatMessage = { id: nextMsgId(), role: 'user', content: trimmed };
      const assistantId = nextMsgId();

      setMessages((prev) => [...prev, userMessage]);
      setIsStreaming(true);

      const controller = new AbortController();
      abortControllerRef.current = controller;

      const allMessages = [...messages, userMessage];

      const ctxResult = getContext ? getContext() : { context, hasTruncatedBlocks: false };
      const resolvedContext = ctxResult?.context;
      const inputTruncated = !!ctxResult?.hasTruncatedBlocks;

      const consumeChunk = createWordBudget();

      streamResponse({
        endpoint,
        messages: allMessages,
        context: resolvedContext,
        signal: controller.signal,
        onChunk: (chunk) => {
          const toAppend = consumeChunk(chunk);
          if (toAppend === null) return;

          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last?.id === assistantId) {
              return [...prev.slice(0, -1), { ...last, content: last.content + toAppend }];
            }
            return [...prev, { id: assistantId, role: 'assistant', content: toAppend }];
          });
        },
        onDone: () => {
          setIsStreaming(false);
          abortControllerRef.current = undefined;
          if (inputTruncated) {
            setMessages((prev) => {
              const last = prev[prev.length - 1];
              if (last?.id !== assistantId) return prev;
              return [...prev.slice(0, -1), { ...last, content: last.content + INPUT_TRUNCATION_NOTE }];
            });
          }
        },
        onError: () => {
          setIsStreaming(false);
          abortControllerRef.current = undefined;
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last?.id === assistantId && !last.content) {
              return [
                ...prev.slice(0, -1),
                {
                  id: assistantId,
                  role: 'assistant' as const,
                  content:
                    "I'm sorry, I couldn't process that request. Please try again or reach out to your Customer Success associate.",
                },
              ];
            }
            if (last?.id !== assistantId) {
              return [
                ...prev,
                {
                  id: assistantId,
                  role: 'assistant' as const,
                  content:
                    "I'm sorry, I couldn't process that request. Please try again or reach out to your Customer Success associate.",
                },
              ];
            }
            return prev;
          });
        },
      });
    },
    [isStreaming, messages, endpoint, context, getContext],
  );

  const reset = useCallback(() => {
    stop();
    setMessages([]);
  }, [stop]);

  return { messages, isStreaming, send, stop, reset };
};

interface StreamOptions {
  endpoint: string;
  messages: ChatMessage[];
  context: string | undefined;
  signal: AbortSignal;
  onChunk: (text: string) => void;
  onDone: () => void;
  onError: (error: unknown) => void;
}

const streamResponse = async ({ endpoint, messages, context, signal, onChunk, onDone, onError }: StreamOptions) => {
  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        messages: messages.map((m) => ({ role: m.role, content: m.content })),
        context,
      }),
      signal,
    });

    if (!response.ok || !response.body) {
      onError(new Error(`HTTP ${response.status}`));
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    let buffer = '';
    let done = false;
    while (!done) {
      // eslint-disable-next-line no-await-in-loop
      const result = await reader.read();
      done = result.done;
      if (result.value) {
        buffer += decoder.decode(result.value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              onDone();
              return;
            }
            try {
              const parsed = JSON.parse(data) as { text?: string; type?: string };
              if (parsed.text) {
                onChunk(parsed.text);
              }
            } catch {
              onChunk(data);
            }
          }
        }
      }
    }

    onDone();
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      return;
    }
    onError(err);
  }
};

export default useChatStream;
